import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import TableActionButton from "@components/buttons/TableActionButton";
import CopyButton from "@components/buttons/CopyButton";
import TransactionStatusClass from "@components/classes/TransactionStatusClass";
import { billingServices } from "@services/apis/Billing";
import { commonApiServices } from "@services/apis/Common";
import { GetBillingData } from "@services/models/Billing/GetBillingModel";
import { GetCountriesListData } from "@services/models/Common/GetCountriesList";
import { ColDef } from "ag-grid-community";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import ConstantFunctions from "../../utils/constants_functions";
import { buildCSV, CSVColumn, downloadCSV } from "../../utils/downloadCSV";

export type BillingHandle = {
  exportToCSV: () => Promise<void>;
};
const LIMIT = 1000;
const Billing = forwardRef<BillingHandle>((_, ref) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const transactionStatusClass = new TransactionStatusClass();
  const [error, setError] = useState<string | null>(null);
  const constantFunctions = new ConstantFunctions();

  const [data, setData] = useState<GetBillingData[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [pdfLoading, setPdfLoading] = useState<{ [key: string]: boolean }>({});
  const [statusLoading, setStatusLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [locationLoading, setLocationLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [countries, setCountries] = useState<GetCountriesListData[]>([]);

  const gridRef = useRef<any>(null);
  const didInitialFetch = useRef(false);
  const inFlight = useRef(false);

  const stopRef = useRef(false);
  const lastRequestedOffsetRef = useRef<number>(-1);

  const fetchBilling = async (nextOffset: number, reset = false) => {
    if (inFlight.current) return;
    if (!reset && stopRef.current) return;
    if (!reset && nextOffset <= lastRequestedOffsetRef.current) return;

    inFlight.current = true;
    lastRequestedOffsetRef.current = nextOffset;

    try {
      setError(null);
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const res = await billingServices.getBilling({
        limit: LIMIT,
        offset: nextOffset,
      });

      const rows: GetBillingData[] = res.data.billing ?? [];
      const p = res.data.pagination;

      const apiHasMore =
        typeof p?.hasMore === "boolean" ? p.hasMore : rows.length === LIMIT;

      if (!apiHasMore || rows.length === 0) {
        stopRef.current = true;
      }

      setHasMore(apiHasMore && rows.length > 0);
      setData((prev) => (reset ? rows : [...prev, ...rows]));
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      inFlight.current = false;
    }
  };

  const handlePdfAction = useCallback(
    async (invoiceId: number, userId: number, isView: boolean) => {
      const key = `${invoiceId}-${isView ? "view" : "download"}`;

      if (pdfLoading[key]) return;

      setPdfLoading((prev) => ({ ...prev, [key]: true }));

      try {
        const { blob: pdfBlob, fileName } =
          await billingServices.sendBillingPdfEmail({
            invoiceId,
            userId,
            returnPdf: true,
          });

        const blob =
          pdfBlob instanceof Blob
            ? pdfBlob
            : new Blob([pdfBlob], { type: "application/pdf" });

        if (isView) {
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 10000);
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName || `invoice-${invoiceId}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Error handling PDF:", error);
      } finally {
        setPdfLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [pdfLoading],
  );

  const handleViewPdf = useCallback(
    (invoiceId: number, userId: number) => {
      handlePdfAction(invoiceId, userId, true);
    },
    [handlePdfAction],
  );

  const handleDownloadPdf = useCallback(
    (invoiceId: number, userId: number) => {
      handlePdfAction(invoiceId, userId, false);
    },
    [handlePdfAction],
  );

  const handleStatusUpdate = useCallback(
    async (invoiceId: number, newStatus: string) => {
      const key = `${invoiceId}-${newStatus}`;

      if (statusLoading[key]) return;

      setStatusLoading((prev) => ({ ...prev, [key]: true }));

      try {
        await billingServices.updateMerchantInvoiceStatus({
          invoiceId,
          status: newStatus as "draft" | "issued" | "paid" | "cancelled",
        });

        setData((prevData) =>
          prevData.map((item) =>
            item.invoiceId === invoiceId
              ? { ...item, status: newStatus }
              : item,
          ),
        );
      } catch (error) {
        console.error("Error updating status:", error);
      } finally {
        setStatusLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [statusLoading],
  );

  const fetchCountries = useCallback(async () => {
    try {
      const response = await commonApiServices.getCountriesList();
      setCountries(response.data);
    } catch (error) {}
  }, []);

  const handleLocationUpdate = useCallback(
    async (
      merchantId: number,
      countryId: number,
      countryName: string,
      invoiceId: number,
    ) => {
      const key = `${invoiceId}-${countryId}`;

      if (locationLoading[key]) return;

      setLocationLoading((prev) => ({ ...prev, [key]: true }));

      try {
        await billingServices.updateMerchantLocation({
          merchantId,
          countryId,
        });

        setData((prevData) =>
          prevData.map((item) =>
            item.invoiceId === invoiceId
              ? { ...item, location: countryName }
              : item,
          ),
        );
      } catch (error) {
        console.error("Error updating location:", error);
      } finally {
        setLocationLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [locationLoading],
  );

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  // initial load
  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;

    // ✅ reset guards
    stopRef.current = false;
    lastRequestedOffsetRef.current = -1;

    setOffset(0);
    setHasMore(true);
    fetchBilling(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // whenever offset increases -> fetch next chunk
  useEffect(() => {
    if (!didInitialFetch.current) return;
    if (offset === 0) return;
    fetchBilling(offset, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const onBodyScroll = useCallback((params: any) => {
    if (stopRef.current || inFlight.current) return;

    const api = params.api;
    const displayedRowCount = api.getDisplayedRowCount();
    const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

    if (
      lastDisplayedRowIndex >= displayedRowCount - 5 &&
      displayedRowCount > 0
    ) {
      setOffset(lastRequestedOffsetRef.current + LIMIT);
    }
  }, []);

  const exportToCSV = async () => {
    const EXPORT_LIMIT = 1000;
    let allRows: GetBillingData[] = [];
    let currentOffset = 0;
    let hasMoreData = true;

    while (hasMoreData) {
      const res = await billingServices.getBilling({
        limit: EXPORT_LIMIT,
        offset: currentOffset,
      });
      const rows = res.data.billing ?? [];
      allRows = [...allRows, ...rows];
      hasMoreData = res.data.pagination?.hasMore ?? false;
      currentOffset += EXPORT_LIMIT;
    }

    const csvColumns: CSVColumn[] = [
      {
        header: "Date",
        field: "issuedAt",
        formatter: (v) => (v ? constantFunctions.dateFormatInDDMMYY(v) : ""),
      },
      { header: "Invoice ID", field: "invoiceId" },
      { header: "Invoice Number", field: "invoiceNumber" },
      { header: "Period", field: "billingMonth" },
      { header: "User ID", field: "userId" },
      { header: "Merchant ID", field: "merchantId" },
      { header: "Legal Entity Name", field: "legalEntityName" },
      { header: "Account Name", field: "merchantName" },
      { header: "DBA", field: "dba" },
      { header: "Location", field: "location" },
      { header: "Service", field: "service" },
      { header: "Direction", field: "direction" },
      { header: "Volume", field: "volume" },
      { header: "Amount", field: "fees" },
      { header: "Currency", field: "currency" },
      { header: "Status", field: "status" },
    ];

    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const csv = buildCSV(allRows, csvColumns);
    downloadCSV(csv, `billing-data-${formattedDate}.csv`);
  };

  useImperativeHandle(ref, () => ({ exportToCSV }));
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "issuedAt",
        width: 100,
        minWidth: 90,
        maxWidth: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        valueFormatter: (p) =>
          p.value ? constantFunctions.dateFormatInDDMMYY(p.value) : "",
      },

      {
        headerName: "Invoice ID",
        field: "invoiceId",
        width: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="Invoice ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Invoice Number",
        field: "invoiceNumber",
        width: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="Invoice Number copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Period",
        field: "billingMonth",
        width: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },

      {
        headerName: "User ID",
        field: "userId",
        width: 100,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="User ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Merchant ID",
        field: "merchantId",
        width: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="Merchant ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Legal Entity Name",
        field: "legalEntityName",
        minWidth: 160,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        valueFormatter: (p) => p.value || "-",
        wrapText: true,
        autoHeight: true,
      },

      {
        headerName: "Account Name",
        field: "merchantName",
        minWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        wrapText: true,
        autoHeight: true,
      },

      {
        headerName: "DBA",
        field: "dba",
        minWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "Location",
        field: "location",
        minWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        cellRenderer: (params: any) => {
          const currentLocation = params.value || "";
          const invoiceId = params.data.invoiceId;
          const merchantId = params.data.merchantId;

          const isLoading = Object.keys(locationLoading).some(
            (key) => key.startsWith(`${invoiceId}-`) && locationLoading[key],
          );

          return (
            <div className="flex items-center gap-2 h-full">
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border border-border-subtle border-t-transparent" />
              ) : (
                <select
                  value={currentLocation}
                  onChange={(e) => {
                    if (e.target.value !== currentLocation) {
                      const selectedCountry = countries.find(
                        (c) => c.country === e.target.value,
                      );
                      if (selectedCountry) {
                        handleLocationUpdate(
                          merchantId,
                          selectedCountry.id,
                          selectedCountry.country,
                          invoiceId,
                        );
                      }
                    }
                  }}
                  className="rounded text-[10px] w-[140px] px-2 py-1 font-medium border border-border-subtle cursor-pointer bg-white"
                  disabled={isLoading}>
                  <option value="">Select Location</option>
                  {countries.map((country) => (
                    <option key={country.countryCode} value={country.country}>
                      {country.country}
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        },
      },

      {
        headerName: "Service",
        field: "service",
        minWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
      },

      {
        headerName: "Direction",
        field: "direction",
        width: 100,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        cellRenderer: ({ data }: any) => {
          const categoryStyle = transactionStatusClass.getTransactionStatusIcon(
            data.direction.toLowerCase(),
          );
          return (
            <div
              className={`inline-flex h-[20px] items-center justify-center gap-1 whitespace-nowrap rounded px-[6px] text-[10px] font-medium ${categoryStyle.bgColor}`}
              style={{ color: categoryStyle.textColor }}>
              {data.direction.toUpperCase() || "-"}
            </div>
          );
        },
      },

      {
        headerName: "Volume",
        field: "volume",
        minWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        valueFormatter: (p) => {
          const v = parseFloat(p.value);
          return isNaN(v) ? p.value : v.toLocaleString();
        },
      },

      {
        headerName: "Amount",
        field: "fees",
        minWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        valueFormatter: (p) => {
          const v = parseFloat(p.value);
          return isNaN(v) ? p.value : `$${v.toLocaleString()}`;
        },
      },

      {
        headerName: "Currency",
        field: "currency",
        width: 95,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
      },

      {
        headerName: "PDF",
        field: "pdf",
        width: 190,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const invoiceId = params.data.invoiceId;
          const userId = params.data.userId;
          const viewKey = `${invoiceId}-view`;
          const downloadKey = `${invoiceId}-download`;

          return (
            <div className="flex items-center gap-1.5 h-full">
              <TableActionButton
                variant="neutral"
                icon="visibility"
                loading={Boolean(pdfLoading[viewKey])}
                onClick={(event) => {
                  event.stopPropagation();
                  handleViewPdf(invoiceId, userId);
                }}
              >
                View
              </TableActionButton>
              <TableActionButton
                variant="primary"
                icon="download"
                loading={Boolean(pdfLoading[downloadKey])}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDownloadPdf(invoiceId, userId);
                }}
              >
                Download
              </TableActionButton>
            </div>
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        width: 130,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          const currentStatus = (params.value || "").toLowerCase();
          const invoiceId = params.data.invoiceId;
          const statusOptions = ["draft", "issued", "paid", "cancelled"];

          const getStatusClass = (status: string) => {
            const upperStatus = status.toUpperCase();
            return upperStatus === "PAID"
              ? "bg-bg-status-green text-text-status-green"
              : upperStatus === "DRAFT" || upperStatus === "ISSUED"
                ? "bg-bg-status-yellow text-text-status-yellow"
                : upperStatus === "CANCELLED"
                  ? "bg-bg-status-red text-text-status-red"
                  : "bg-bg-status-yellow text-text-status-yellow";
          };

          const isLoading = Object.keys(statusLoading).some(
            (key) => key.startsWith(`${invoiceId}-`) && statusLoading[key],
          );

          return (
            <div className="flex items-center gap-2 h-full">
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border border-gray-400 border-t-transparent" />
              ) : (
                <select
                  value={currentStatus}
                  onChange={(e) => {
                    if (e.target.value !== currentStatus) {
                      handleStatusUpdate(invoiceId, e.target.value);
                    }
                  }}
                  className={`rounded text-[8px] px-2 py-1 font-medium border-none cursor-pointer ${getStatusClass(currentStatus)}`}
                  disabled={isLoading}>
                  {statusOptions.map((option) => (
                    <option
                      key={option}
                      value={option}
                      className="bg-white text-black">
                      {option.toUpperCase()}
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        },
      },
    ],
    [
      pdfLoading,
      statusLoading,
      locationLoading,
      countries,
      handleViewPdf,
      handleDownloadPdf,
      handleStatusUpdate,
      handleLocationUpdate,
    ],
  );

  return (
    <div
      className="flex-1 py-4 overflow-hidden"
      style={{ height: "calc(100dvh - 70px)" }}>
      {loading && offset === 0 ? (
        <AgGridSkeleton
          columnDefs={columnDefs}
          style={{ height: "calc(100vh - 150px)" }}
        />
      ) : (
        <div className="flex flex-col h-full">
          <AgGridReactTable
            ref={gridRef}
            rowData={data}
            columnDefs={columnDefs}
            onBodyScroll={onBodyScroll}
            error={error ?? undefined}
            style={{ height: "calc(100vh - 150px)" }}
            getRowId={(params) => params.data.invoiceId?.toString()} // recommended
          />

          {loadingMore && data.length > 0 && (
            <div className="flex items-center justify-center py-4 border-t border-border-subtle">
              <div className="flex items-center space-x-2 text-text-subtle">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary" />
                <span className="text-sm">Loading more billing...</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

Billing.displayName = "Billing";

export default Billing;
